import {
  forwardRef,
  // eslint-disable-next-line bh/banned-chakra-import
  IconButton as BaseButton,
  IconButtonProps as BaseButtonProps,
} from "@chakra-ui/react";
import React from "react";

import { useForwardedRef } from "../../hooks/useForwardedRef";
import { GAEventDetails, useSendGAEvent } from "../../utils/googleAnalytics";
import { getEventValues } from "./utils";

type IconButtonProps = BaseButtonProps & {
  /** Details to be passed to `sendGaEvent`. See component description for default values  */
  bhEvent?: GAEventDetails;
  /**
   * Set to `false` to opt out of the automatic `"click"` GA event. Useful when
   * the caller's `onClick` fires a more specific event and a generic click
   * event would just be noise. Defaults to `true`.
   */
  shouldSendGAEvent?: boolean;
};

/**
 * Wrapper around the chakra `<IconButton />` that sends ga events on click.
 *
 * You can use the `bhEvent` prop to control the ga event details.
 * The following defaults are applied:
 *  - action defaults `"click"`
 *  - label defaults to `data-testid` attribute (if present), or the button's inner text
 *
 * Pass `shouldSendGAEvent={false}` to suppress the automatic event entirely.
 */
export const IconButton = forwardRef<IconButtonProps, "button">(
  ({ bhEvent, shouldSendGAEvent = true, onClick, ...props }, forwardedRef) => {
    const sendGaEvent = useSendGAEvent();
    const ref = useForwardedRef<HTMLButtonElement>(forwardedRef);

    return (
      <BaseButton
        {...props}
        ref={ref}
        onClick={(e) => {
          if (props.isDisabled) return;
          if (shouldSendGAEvent) {
            const { action, category, label, params, value } = getEventValues({
              el: ref.current,
              componentType: "icon_button",
              providedParams: bhEvent,
            });

            sendGaEvent(action, category, label, value, params);
          }
          onClick?.(e);
        }}
      />
    );
  }
);
